import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ServiceGroup, ServiceEventType } from '@vendasta/meetings';
import { durationMinutes } from '../../shared/duration.utils';
import { MAX_BATCH_EVENTS, isEventUnavailable } from '../../util';

export interface GroupServicesDialogData {
  group: ServiceGroup;
  isMultiSelect: boolean;
  disabledEventIds: Set<string>;
  preSelectedEventIds: Set<string>;
  serviceBatchBookingEnabled: boolean;
  maxEvents?: number;
  totalSelectedCount?: number;
  preSelectedFromGroupCount?: number;
}

@Component({
  selector: 'app-group-services-dialog',
  standalone: false,
  templateUrl: './group-services-dialog.component.html',
  styleUrls: ['./group-services-dialog.component.scss'],
})
export class GroupServicesDialogComponent {
  group: ServiceGroup;
  isMultiSelect: boolean;
  disabledEventIds: Set<string>;
  serviceBatchBookingEnabled: boolean;
  private maxEvents: number;
  private totalSelectedCount: number;
  private preSelectedFromGroupCount: number;

  /** Single-select mode: one selected service */
  selectedService: ServiceEventType | null = null;

  /** Multi-select mode: set of selected service IDs */
  selectedServiceIds = new Set<string>();
  selectedServices: ServiceEventType[] = [];
  hasPreSelection = false;

  constructor(
    private readonly dialogRef: MatDialogRef<GroupServicesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GroupServicesDialogData
  ) {
    this.group = data.group;
    this.isMultiSelect = data.isMultiSelect ?? false;
    this.disabledEventIds = data.disabledEventIds ?? new Set();
    this.serviceBatchBookingEnabled = data.serviceBatchBookingEnabled ?? false;
    this.maxEvents = data.maxEvents ?? MAX_BATCH_EVENTS;
    this.totalSelectedCount = data.totalSelectedCount ?? 0;
    this.preSelectedFromGroupCount = data.preSelectedFromGroupCount ?? 0;

    const preSelected = data.preSelectedEventIds ?? new Set();
    this.hasPreSelection = preSelected.size > 0;
    if (preSelected.size && this.group.eventTypes?.length) {
      const preSelectedEvents = this.group.eventTypes.filter((e) =>
        preSelected.has(e.id)
      );
      if (this.isMultiSelect) {
        preSelectedEvents.forEach((e) => {
          this.selectedServiceIds.add(e.id);
          this.selectedServices.push(e);
        });
      } else if (preSelectedEvents.length) {
        this.selectedService = preSelectedEvents[0];
      }
    }
  }

  get isMaxReached(): boolean {
    const currentCount = this.isMultiSelect ? this.selectedServiceIds.size : (this.selectedService ? 1 : 0);
    const otherSelectedCount = this.totalSelectedCount - this.preSelectedFromGroupCount;
    return (otherSelectedCount + currentCount) >= this.maxEvents;
  }

  isEventUnavailable(service: ServiceEventType): boolean {
    return isEventUnavailable(service);
  }

  isDisabled(service: ServiceEventType): boolean {
    if (this.isEventUnavailable(service)) {
      return true;
    }
    if (
      this.selectedServiceIds.has(service.id) ||
      this.selectedService?.id === service.id
    ) {
      return false;
    }
    if (this.disabledEventIds.has(service.id)) {
      return true;
    }
    if (this.isMaxReached) {
      return true;
    }
    if (!this.serviceBatchBookingEnabled && this.hasSelection) {
      return true;
    }
    return false;
  }

  onSelectService(service: ServiceEventType): void {
    if (this.isDisabled(service)) {
      return;
    }
    this.selectedService = service;
  }

  onToggleService(service: ServiceEventType): void {
    if (this.isDisabled(service)) {
      return;
    }
    if (this.selectedServiceIds.has(service.id)) {
      this.selectedServiceIds.delete(service.id);
      this.selectedServices = this.selectedServices.filter(
        (s) => s.id !== service.id
      );
    } else {
      this.selectedServiceIds.add(service.id);
      this.selectedServices.push(service);
    }
  }

  isServiceChecked(service: ServiceEventType): boolean {
    return this.selectedServiceIds.has(service.id);
  }

  get hasSelection(): boolean {
    return this.isMultiSelect
      ? this.selectedServices.length > 0
      : this.selectedService !== null;
  }

  durationMinutes = durationMinutes;

  onCancel(): void {
    this.dialogRef.close();
  }

  onAdd(): void {
    if (this.isMultiSelect) {
      this.dialogRef.close(this.selectedServices);
    } else {
      this.dialogRef.close(this.selectedService);
    }
  }
}
